import { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const currentSlug = slug || categories[0].slug;
  const [activeSlug, setActiveSlug] = useState(currentSlug);

  useEffect(() => {
    setActiveSlug(currentSlug);
  }, [currentSlug]);

  const handleTabClick = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
  };

  const filteredArticles = useMemo(() => {
    return articles
      .filter((a) => a.categorySlug === activeSlug)
      .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91heilia.com/' },
              { '@type': 'ListItem', position: 2, name: activeCategory?.name || '分类', item: `https://91heilia.com/category/${activeSlug}` },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${activeCategory?.name || '分类'} - 91黑料`,
            description: activeCategory?.description || '91黑料分类页面',
            url: `https://91heilia.com/category/${activeSlug}`,
            isPartOf: { '@type': 'WebSite', name: '91黑料', url: 'https://91heilia.com/' },
          }),
        }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: activeCategory?.name || '分类' },
          ]}
        />

        {/* Category Title */}
        <div className="mt-6 mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50">
            {activeCategory?.name || '分类'}
          </h1>
          <p className="mt-2 text-sm text-foreground-600 max-w-2xl">
            {activeCategory?.description}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleTabClick(cat.slug)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeSlug === cat.slug
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 border border-background-300/40 text-foreground-500 hover:text-foreground-200 hover:border-background-500/40'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`${cat.icon} text-xs`}></i>
              </span>
              {cat.name}
            </button>
          ))}
        </div>

        {/* Articles Grid */}
        {filteredArticles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group block bg-background-100 border border-background-300/40 rounded-xl overflow-hidden hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full h-[200px] overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {article.isHot && (
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-500/15 text-primary-500 border border-primary-500/20 whitespace-nowrap">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-fire-fill text-[10px]"></i>
                      </span>
                      HOT
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <span className="text-xs text-primary-500 font-medium">{article.categoryName}</span>
                  <h3 className="text-sm font-semibold text-foreground-200 line-clamp-2 mt-1.5 leading-snug group-hover:text-primary-500 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-600 line-clamp-2 mt-2">{article.excerpt}</p>
                  <div className="flex items-center gap-3 mt-3 text-xs text-foreground-700">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-eye-line text-[10px]"></i>
                      </span>
                      {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-chat-3-line text-[10px]"></i>
                      </span>
                      {article.commentCount}
                    </span>
                    <span className="ml-auto">{article.publishedAt.split('T')[0]}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-700">
              <i className="ri-inbox-line text-4xl"></i>
            </span>
            <p className="text-foreground-600">该分类暂无内容，请查看其他分类</p>
          </div>
        )}
      </div>
    </>
  );
}